import { Badge } from "@circleco/compass/components/Badge";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import {
  dateFormats,
  formattedDateTime,
} from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import type { UpgradePreview } from "@/react/types/Paywalls/Checkout/UpgradePreview";

import { uniqueUpgradeCoupons } from "./helpers/uniqueUpgradeCoupons";
import { UpgradeOrderCouponChip } from "./UpgradeOrderCouponChip";
import { UpgradeOrderRow } from "./UpgradeOrderRow";

const i18nRoot = "paywall_checkout.offer_page.upgrade";
const renewalDate = (at: string) =>
  formattedDateTime({
    dateTime: new Date(at),
    format: dateFormats.short_month_and_date_with_year,
  });

export const UpgradeTrialOrderDetails = ({
  preview,
}: {
  preview: UpgradePreview;
}) => {
  const trialDays =
    preview.trial.disposition === "target"
      ? preview.trial.target_days
      : undefined;
  const isEstimate =
    preview.execution_lane === "in_place" &&
    preview.trial.disposition === "target";
  const discounts = preview.coupon.effective_discounts.filter(
    discount => (discount.first_paid_renewal_amount?.amount ?? 0) > 0,
  );
  const paymentLabel = trialDays
    ? t("paywall_checkout.future_payments.future_payments_after_x_trial_days", {
        count: trialDays,
      })
    : t([i18nRoot, "payment_after_trial"]);

  return (
    <div className="space-y-6">
      {isEstimate && (
        <Typography
          variant="body-sm"
          color="secondary"
          data-testid="upgrade-estimated-terms-disclaimer"
        >
          {t([i18nRoot, "estimated_amounts_note"])}
        </Typography>
      )}
      <div className="space-y-3" data-testid="upgrade-order-details-rows">
        <Typography variant="body-md">
          {t("paywall_checkout.todays_payment")}
        </Typography>
        <UpgradeOrderRow
          label={
            trialDays
              ? t("paywall_checkout.free_trial_day", { count: trialDays })
              : t("free_trial")
          }
          amount={preview.today.total}
        />
      </div>
      <div className="space-y-3 border-t border-primary pt-6">
        <UpgradeOrderRow
          label={
            isEstimate
              ? t([i18nRoot, "estimated_payment_after_trial"], {
                  payment: paymentLabel,
                })
              : paymentLabel
          }
          amount={preview.first_paid_renewal.amount}
          variant="plan"
          detail={
            !preview.first_paid_renewal.amount
              ? t([i18nRoot, "renewal_amount_unavailable"])
              : undefined
          }
        />
        <UpgradeOrderRow
          label={t([
            "settings.paywall_prices.subscription.regular_price",
            preview.destination.interval_type,
          ])}
          amount={preview.destination.list_amount}
        />
        {discounts.map(discount => (
          <div key={discount.discount_id} className="space-y-3">
            <UpgradeOrderRow
              label={t([i18nRoot, "coupon_applied"])}
              amount={
                discount.first_paid_renewal_amount && {
                  amount: -discount.first_paid_renewal_amount.amount,
                  formatted: `-${discount.first_paid_renewal_amount.formatted}`,
                }
              }
              variant="discount"
            />
          </div>
        ))}
        {uniqueUpgradeCoupons(discounts).map(discount => (
          <UpgradeOrderCouponChip
            key={discount.discount_id}
            discount={discount}
          />
        ))}
        <UpgradeOrderRow
          label={t([i18nRoot, "first_payment_on"], {
            date: renewalDate(preview.first_paid_renewal.at),
          })}
          amount={preview.first_paid_renewal.amount}
        />
        {preview.later_undiscounted_renewal && (
          <UpgradeOrderRow
            label={preview.later_undiscounted_renewal.label}
            amount={preview.later_undiscounted_renewal.amount}
            detail={t([i18nRoot, "plan_renews_on"], {
              date: renewalDate(preview.later_undiscounted_renewal.at),
            })}
          />
        )}
      </div>
      <div className="border-t border-primary pt-6">
        <Typography variant="body-sm" color="secondary">
          {t("paywall_checkout.payment_schedule", {
            payment_sentence: preview.destination.billing_label,
          })}
        </Typography>
      </div>
      <div
        className="border-t border-primary py-6"
        data-testid="upgrade-order-details-totals"
      >
        <UpgradeOrderRow
          label={
            <div className="inline-flex flex-wrap items-center gap-2">
              <strong>
                {t([
                  i18nRoot,
                  isEstimate ? "estimated_total_due_today" : "total_due_today",
                ])}
              </strong>
              <Badge label={t("free_trial")} variant="carolinaBlue" />
            </div>
          }
          amount={preview.today.total}
          variant="total"
        />
      </div>
    </div>
  );
};
